import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lato:wght@300;400;700;900&display=swap');

  :root {
    --russian-red:  #CD0302;
    --sunburst:     #F0C400;
    --edam:         #F5D19D;
    --citrusy:      #F88702;
    --green:        #4F7E17;
    --green-light:  #6aad1f;
    --edam-light:   #fdf3e3;
    --edam-dark:    #e8c070;
    --soil:         #3d2b1f;
    --cream:        #fffdf5;
    --fog:          #f7f2ea;
  }

  .au-root {
    font-family: 'Lato', sans-serif;
    background:
      radial-gradient(ellipse at 5% 0%,   rgba(79,126,23,0.09)  0%, transparent 50%),
      radial-gradient(ellipse at 95% 100%, rgba(240,196,0,0.08)  0%, transparent 50%),
      var(--cream);
    min-height: 100vh;
    padding: 40px 16px 72px;
    color: var(--soil);
  }

  .au-inner { max-width: 860px; margin: 0 auto; }

  /* ─── Loading ─── */
  .au-loading {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    min-height: 60vh; gap: 18px;
  }
  @keyframes spin  { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.5;transform:scale(.85);} }

  .au-spinner-wrap  { position:relative; width:56px; height:56px; }
  .au-spinner {
    position:absolute; inset:0; border-radius:50%;
    border:3px solid rgba(79,126,23,.15);
    border-top-color:var(--green);
    animation:spin .8s linear infinite;
  }
  .au-spinner-inner {
    position:absolute; top:50%; left:50%;
    transform:translate(-50%,-50%);
    width:24px; height:24px;
    background:var(--sunburst); border-radius:50%;
    opacity:.7; animation:pulse 1.2s ease-in-out infinite;
  }
  .au-loading-text {
    font-size:.92rem; font-weight:700;
    color:var(--green); letter-spacing:.06em;
    animation:pulse 1.2s ease-in-out infinite;
  }

  /* ─── Error ─── */
  .au-error-wrap { display:flex; align-items:center; justify-content:center; min-height:60vh; }
  .au-error-box {
    background:rgba(205,3,2,.05);
    border:1.5px solid rgba(205,3,2,.25);
    border-radius:16px; padding:32px 40px;
    text-align:center; max-width:400px;
  }
  .au-error-text { font-size:.97rem; color:var(--russian-red); font-weight:700; }

  /* ─── Header ─── */
  .au-header {
    margin-bottom:40px; padding-bottom:28px;
    border-bottom:2px dashed var(--edam-dark);
    position:relative;
  }
  .au-eyebrow {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--green); color:#fff;
    font-size:.68rem; font-weight:900;
    letter-spacing:.18em; text-transform:uppercase;
    padding:5px 14px; border-radius:20px; margin-bottom:16px;
  }
  .au-title {
    font-family:'Playfair Display',Georgia,serif;
    font-size:clamp(2rem,5vw,3rem);
    font-weight:700; font-style:italic;
    color:var(--soil); line-height:1.15; margin-bottom:8px;
  }
  .au-title span { color:var(--green); }
  .au-subtitle {
    font-size:.9rem; color:#9a8878;
    font-style:italic; letter-spacing:.03em;
  }
  .au-header-deco {
    position:absolute; right:0; top:0;
    width:100px; height:100px;
    background:radial-gradient(circle,rgba(240,196,0,.18) 0%,transparent 70%);
    border-radius:50%; pointer-events:none;
  }

  /* ─── Sections ─── */
  .au-section {
    margin-bottom:32px; padding-bottom:32px;
    border-bottom:1px dashed var(--edam-dark);
  }
  .au-section:last-of-type { border-bottom:none; margin-bottom:0; padding-bottom:0; }

  .au-section-header {
    display:flex; align-items:center; gap:12px; margin-bottom:18px;
  }
  .au-section-icon {
    width:36px; height:36px; border-radius:10px;
    display:flex; align-items:center; justify-content:center;
    font-size:1rem; flex-shrink:0;
  }
  .au-section-icon.green  { background:rgba(79,126,23,.12);  color:var(--green); }
  .au-section-icon.orange { background:rgba(248,135,2,.12);  color:var(--citrusy); }
  .au-section-icon.yellow { background:rgba(240,196,0,.18);  color:#b08c00; }
  .au-section-title {
    font-family:'Playfair Display',Georgia,serif;
    font-size:1.35rem; font-weight:700; color:var(--soil);
  }

  /* ─── Intro strip ─── */
  .au-intro-strip {
    background:linear-gradient(135deg,rgba(79,126,23,.07) 0%,rgba(240,196,0,.07) 100%);
    border-left:4px solid var(--green);
    border-radius:0 14px 14px 0;
    padding:20px 22px;
    font-size:.97rem; color:#5a4a3a; line-height:1.75;
    margin-bottom:16px;
  }
  .au-desc {
    font-size:.95rem; color:#6a5a48; line-height:1.8;
  }

  /* ─── Mission cards ─── */
  .au-mission-card {
    display:flex; align-items:flex-start; gap:14px;
    padding:16px 18px;
    background:rgba(79,126,23,.05);
    border:1.5px solid rgba(79,126,23,.15);
    border-radius:12px;
    margin-bottom:10px;
    transition:transform .15s, box-shadow .15s;
  }
  .au-mission-card:hover {
    transform:translateX(4px);
    box-shadow:0 3px 14px rgba(79,126,23,.12);
  }
  .au-mission-icon {
    flex-shrink:0; width:28px; height:28px; border-radius:50%;
    background:var(--green); color:#fff;
    display:flex; align-items:center; justify-content:center;
    font-size:.85rem; font-weight:900; margin-top:2px;
  }
  .au-mission-title {
    font-weight:700; font-size:.95rem;
    color:var(--soil); margin-bottom:4px;
  }
  .au-mission-desc { font-size:.88rem; color:#6a5a48; line-height:1.6; }

  /* ─── Commitment ─── */
  .au-commitment {
    background:var(--fog);
    border:1.5px solid var(--edam-dark);
    border-radius:14px; padding:20px 22px;
    font-size:.95rem; color:#5a4a3a; line-height:1.75;
  }

  /* ─── Contact CTA ─── */
  .au-contact-cta {
    background:linear-gradient(135deg,rgba(248,135,2,.08) 0%,rgba(240,196,0,.1) 100%);
    border-left:4px solid var(--citrusy);
    border-radius:0 14px 14px 0;
    padding:18px 22px;
    font-size:.92rem; color:#5a4a3a; line-height:1.7;
  }

  /* ─── Stats ─── */
  .au-stats-grid {
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(200px,1fr));
    gap:18px;
    margin:8px 0;
  }
  .au-stat-card {
    background:#fff;
    border:1.5px solid var(--edam);
    border-radius:16px;
    padding:32px 20px;
    text-align:center;
    box-shadow:0 2px 12px rgba(79,126,23,.07);
    transition:transform .2s, box-shadow .2s;
    position:relative; overflow:hidden;
  }
  .au-stat-card::before {
    content:'';
    position:absolute; top:0; left:0; right:0;
    height:4px; border-radius:16px 16px 0 0;
  }
  .au-stat-card.s1::before { background:linear-gradient(90deg,var(--green),var(--green-light)); }
  .au-stat-card.s2::before { background:linear-gradient(90deg,var(--citrusy),var(--russian-red)); }
  .au-stat-card.s3::before { background:linear-gradient(90deg,var(--sunburst),var(--citrusy)); }

  .au-stat-card:hover {
    transform:translateY(-4px);
    box-shadow:0 8px 24px rgba(79,126,23,.14);
  }
  .au-stat-number {
    font-family:'Playfair Display',serif;
    font-size:2.8rem; font-weight:700;
    line-height:1.1; margin-bottom:10px;
  }
  .au-stat-card.s1 .au-stat-number { color:var(--green); }
  .au-stat-card.s2 .au-stat-number { color:var(--russian-red); }
  .au-stat-card.s3 .au-stat-number { color:#b08c00; }
  .au-stat-label {
    font-size:.85rem; font-weight:700;
    color:#9a8878; letter-spacing:.05em; text-transform:uppercase;
  }

  /* ─── Brands ─── */
  .au-brands-title {
    font-family:'Playfair Display',serif;
    font-size:1.35rem; font-weight:700;
    color:var(--soil); text-align:center; margin-bottom:24px;
    font-style:italic;
  }
  .au-brands-title span { color:var(--green); }

  .au-brands-wrap { position:relative; }

  .au-brands-scroll-btn {
    position:absolute;
    top:50%; transform:translateY(-50%);
    width:42px; height:42px;
    border-radius:50%; border:none;
    display:flex; align-items:center; justify-content:center;
    cursor:pointer; z-index:10;
    transition:transform .2s, box-shadow .2s;
  }
  .au-brands-scroll-btn.left  { left:-16px; }
  .au-brands-scroll-btn.right { right:-16px; }
  .au-brands-scroll-btn.green-btn {
    background:linear-gradient(135deg,var(--green),var(--green-light));
    box-shadow:0 4px 14px rgba(79,126,23,.35);
    color:#fff;
  }
  .au-brands-scroll-btn.green-btn:hover {
    transform:translateY(-50%) scale(1.1);
    box-shadow:0 6px 20px rgba(79,126,23,.45);
  }

  #brands-container {
    display:flex; gap:20px;
    overflow-x:auto; scroll-behavior:smooth;
    scrollbar-width:none;
    padding:16px 0;
    -webkit-overflow-scrolling:touch;
  }
  #brands-container::-webkit-scrollbar { display:none; }

  .au-brand-card {
    min-width:140px; height:100px;
    background:#fff;
    border:1.5px solid var(--edam);
    border-radius:12px;
    display:flex; align-items:center; justify-content:center;
    padding:18px;
    box-shadow:0 2px 8px rgba(79,126,23,.06);
    cursor:pointer;
    transition:transform .2s, box-shadow .2s, border-color .2s;
    flex-shrink:0;
  }
  .au-brand-card:hover {
    transform:translateY(-4px);
    box-shadow:0 6px 18px rgba(79,126,23,.14);
    border-color:var(--edam-dark);
  }
  .au-brand-card img {
    max-width:100%; max-height:52px;
    object-fit:contain;
  }

  /* ─── Scroll animation ─── */
  .au-anim {
    opacity:0; transform:translateY(16px);
    transition:opacity .65s ease-out, transform .65s ease-out;
  }
  .au-anim.visible { opacity:1; transform:none; }
`;

/* ── AnimatedOnScroll (unchanged logic) ── */
const AnimatedOnScroll = ({ children, animationClass, delay = 0 }) => {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
      }
    }, { threshold: 0.15 });

    if (domRef.current) observer.observe(domRef.current);

    return () => {
      if (domRef.current) observer.unobserve(domRef.current);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`au-anim ${isVisible ? 'visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ── AboutUsPage ── */
const AboutUsPage = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [language, setLanguage]   = useState('bn');

  const defaultBrands = [
    { name: 'Vim',         image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand21.png' },
    { name: 'Bashundhara', image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand20.png' },
    { name: 'Dano',        image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand19.png' },
    { name: 'Xiaomi',      image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand18.png' },
    { name: 'Radhuni',     image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand17.png' },
    { name: 'Lux',         image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand16.png' },
    { name: 'Walton',      image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand15.png' },
    { name: 'Danish',      image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand14.png' },
    { name: 'Asus',        image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand13.png' },
    { name: 'Chashi',      image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand12.png' },
    { name: 'Rupchada',    image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand11.png' },
    { name: 'Garnier',     image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand10.png' },
    { name: 'Nestle',      image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand9.png'  },
    { name: 'Horlicks',    image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand8.png'  },
    { name: 'Wheel',       image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand7.png'  },
    { name: 'Unilever',    image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand6.png'  },
    { name: 'Lifeboy',     image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand5.png'  },
    { name: 'Fresh',       image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand4.png'  },
    { name: 'ACI',         image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand3.png'  },
    { name: 'Teer',        image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand2.png'  },
    { name: 'Diploma',     image: 'https://react-easy-commere.easytechsolutions.xyz/img/compressed_brand_brand1.png'  },
  ];

  const [brands, setBrands] = useState(defaultBrands);

  useEffect(() => {
    const fetchAboutUsData = async () => {
      try {
        const response = await axios.get('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/about-us');
        if (response.data.success) {
          setAboutData(response.data.aboutUs);
          if (response.data.aboutUs.brands && response.data.aboutUs.brands.length > 0) {
            setBrands(response.data.aboutUs.brands);
          }
        }
      } catch (err) {
        console.error('Error fetching About Us data:', err);
        setError('Failed to load About Us data');
      } finally {
        setLoading(false);
      }
    };
    fetchAboutUsData();
  }, []);

  /* ── Loading ── */
  if (loading) return (
    <>
      <style>{styles}</style>
      <div className="au-root">
        <div className="au-loading">
          <div className="au-spinner-wrap">
            <div className="au-spinner" />
            <div className="au-spinner-inner" />
          </div>
          <p className="au-loading-text">Loading...</p>
        </div>
      </div>
    </>
  );

  /* ── Error ── */
  if (error || !aboutData) return (
    <>
      <style>{styles}</style>
      <div className="au-root">
        <div className="au-error-wrap">
          <div className="au-error-box">
            <p className="au-error-text">{error || 'Data not available'}</p>
          </div>
        </div>
      </div>
    </>
  );

  /* ── Page ── */
  return (
    <>
      <style>{styles}</style>
      <div className="au-root">
        <div className="au-inner">

          {/* ── Header ── */}
          <AnimatedOnScroll>
            <div className="au-header">
              <div className="au-header-deco" />
              <div className="au-eyebrow">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z"/>
                </svg>
                About Us
              </div>
              <h1 className="au-title">
                {aboutData.title
                  ? <>{aboutData.title.split(' ').slice(0,-1).join(' ')} <span>{aboutData.title.split(' ').slice(-1)}</span></>
                  : <>About <span>Us</span></>
                }
              </h1>
              {aboutData.subtitle && (
                <p className="au-subtitle">🌿 {aboutData.subtitle}</p>
              )}
            </div>
          </AnimatedOnScroll>

          {/* ── Introduction ── */}
          <AnimatedOnScroll delay={80}>
            <div className="au-section">
              <div className="au-intro-strip">
                {aboutData.introduction}
              </div>
              {aboutData.description && (
                <p className="au-desc" style={{ marginTop: '14px' }}>
                  {aboutData.description}
                </p>
              )}
            </div>
          </AnimatedOnScroll>

          {/* ── Our Mission ── */}
          {aboutData.missionItems && aboutData.missionItems.length > 0 && (
            <AnimatedOnScroll delay={120}>
              <div className="au-section">
                <div className="au-section-header">
                  <div className="au-section-icon green">🌱</div>
                  <h2 className="au-section-title">Our Mission</h2>
                </div>
                <div>
                  {aboutData.missionItems.map((mission, index) => (
                    <AnimatedOnScroll key={index} delay={140 + index * 60}>
                      <div className="au-mission-card">
                        <div className="au-mission-icon">✓</div>
                        <div>
                          <p className="au-mission-title">{mission.title}</p>
                          <p className="au-mission-desc">{mission.description}</p>
                        </div>
                      </div>
                    </AnimatedOnScroll>
                  ))}
                </div>
              </div>
            </AnimatedOnScroll>
          )}

          {/* ── Commitment ── */}
          {aboutData.commitment && (
            <AnimatedOnScroll delay={200}>
              <div className="au-section">
                <div className="au-section-header">
                  <div className="au-section-icon orange">🤝</div>
                  <h2 className="au-section-title">Our Commitment</h2>
                </div>
                <div className="au-commitment">
                  {aboutData.commitment}
                </div>
              </div>
            </AnimatedOnScroll>
          )}

          {/* ── Contact CTA ── */}
          {aboutData.contactCta && (
            <AnimatedOnScroll delay={240}>
              <div className="au-section">
                <div className="au-contact-cta">
                  {aboutData.contactCta}
                </div>
              </div>
            </AnimatedOnScroll>
          )}

          {/* ── Stats ── */}
          <AnimatedOnScroll delay={280}>
            <div className="au-section">
              <div className="au-section-header">
                <div className="au-section-icon yellow">📊</div>
                <h2 className="au-section-title">By the Numbers</h2>
              </div>
              <div className="au-stats-grid">
                <div className="au-stat-card s1">
                  <div className="au-stat-number">{aboutData.stats?.yearsExperience || '10+'}</div>
                  <div className="au-stat-label">Years Experience</div>
                </div>
                <div className="au-stat-card s2">
                  <div className="au-stat-number">{aboutData.stats?.happyCustomers || '50K+'}</div>
                  <div className="au-stat-label">Happy Customers</div>
                </div>
                <div className="au-stat-card s3">
                  <div className="au-stat-number">{aboutData.stats?.productsCount || '100+'}</div>
                  <div className="au-stat-label">Products</div>
                </div>
              </div>
            </div>
          </AnimatedOnScroll>

          {/* ── Brands ── */}
          <AnimatedOnScroll delay={320}>
            <div className="au-section">
              <div className="au-section-header">
                <div className="au-section-icon green">🏷</div>
                <h2 className="au-section-title">Our Trusted <span style={{ color: 'var(--green)' }}>Brands</span></h2>
              </div>

              <div className="au-brands-wrap">
                {/* Left btn */}
                <button
                  className="au-brands-scroll-btn left green-btn"
                  onClick={() => {
                    const c = document.getElementById('brands-container');
                    c.scrollBy({ left: -200, behavior: 'smooth' });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>

                {/* Scroll container */}
                <div id="brands-container">
                  {brands.map((brand, index) => (
                    <div key={index} className="au-brand-card">
                      <img alt={brand.name} src={brand.image} />
                    </div>
                  ))}
                </div>

                {/* Right btn */}
                <button
                  className="au-brands-scroll-btn right green-btn"
                  onClick={() => {
                    const c = document.getElementById('brands-container');
                    c.scrollBy({ left: 200, behavior: 'smooth' });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </AnimatedOnScroll>

        </div>
      </div>
    </>
  );
};

export default AboutUsPage;